import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { authService } from '../../../services/authService';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [successMessage, setSuccessMessage] = useState('');

  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) score++;
    if (/\d/?.test(password)) score++;
    if (/[^a-zA-Z0-9]/?.test(password)) score++;

    const strengthMap = {
      0: { label: '', color: '' },
      1: { label: 'Very Weak', color: 'bg-error' },
      2: { label: 'Weak', color: 'bg-warning' },
      3: { label: 'Fair', color: 'bg-warning' },
      4: { label: 'Good', color: 'bg-success' },
      5: { label: 'Strong', color: 'bg-success' }
    };

    return { score, ...strengthMap?.[score] };
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setSuccessMessage('');

    const { data, error } = await authService?.signUp(
      formData?.email,
      formData?.password,
      formData?.fullName
    );

    if (error) {
      setErrors({
        submit: error?.message || 'Registration failed. Please try again.'
      });
      setLoading(false);
    } else {
      setSuccessMessage('Account created successfully! Please check your email to verify your account.');
      setLoading(false);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  const handleGoogleSignup = () => {
    setLoading(true);
    setTimeout(() => {
      console.log('Google signup initiated');
      navigate('/dashboard');
    }, 1000);
  };

  return (
    <div className="w-full">
      {successMessage && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-12 mb-24 flex items-start gap-8">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-2" />
          <p className="text-sm text-success font-caption">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-20">
        <Input
          type="text"
          label="Full Name"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={(e) => handleInputChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          required
          disabled={loading}
        />

        <Input
          type="email"
          label="Email Address"
          placeholder="Enter your email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
          disabled={loading}
        />

        <div>
          <Input
            type="password"
            label="Password"
            placeholder="Create a strong password"
            description="Must be at least 8 characters with uppercase, lowercase, and number"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
            disabled={loading}
          />
          
          {formData?.password && passwordStrength?.label && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-caption text-muted-foreground">Password Strength:</span>
                <span className={`text-xs font-caption font-medium ${
                  passwordStrength?.score >= 4 ? 'text-success' : 
                  passwordStrength?.score >= 3 ? 'text-warning' : 'text-error'
                }`}>
                  {passwordStrength?.label}
                </span>
              </div>
              <div className="h-6 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                  style={{ width: `${(passwordStrength?.score / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <Input
          type="password"
          label="Confirm Password"
          placeholder="Re-enter your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
          disabled={loading}
        />

        <div className="space-y-12">
          <Checkbox
            label={
              <span className="text-sm">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => console.log('Terms clicked')}
                  className="text-primary hover:text-accent transition-smooth hover:underline"
                >
                  Terms of Service
                </button>
                {' '}and{' '}
                <button
                  type="button"
                  onClick={() => console.log('Privacy clicked')}
                  className="text-primary hover:text-accent transition-smooth hover:underline"
                >
                  Privacy Policy
                </button>
              </span>
            }
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e?.target?.checked);
              if (errors?.terms) {
                setErrors(prev => ({ ...prev, terms: '' }));
              }
            }}
            error={errors?.terms}
            disabled={loading}
          />
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-12 flex items-start gap-8">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-2" />
            <p className="text-sm text-error font-caption">{errors?.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          disabled={loading}
          iconName="UserPlus"
          iconPosition="left"
          iconSize={20}
        >
          Create Account
        </Button>
      </form>
    </div>
  );
};

export default RegistrationForm;